import { CompanyEarningConfig } from './company-earning-config';
import { CompanyDeductionConfig } from './company-deduction-config';
import { IdNamePair } from './id-name-pair';
import { ErrorDetail } from './error-detail';
import { PayrollFrequency } from './payroll-info';
import { CompanyLocationConfig } from './company-location-config';
import { CompanyTaxCodeConfig } from './tax-config';
import { DatePipe } from '@angular/common';
import { QuarterStatuses } from './quarters';
import { ApiResponse } from './api-response';

export class AddPaylineRequestModel {
  HistoryVoidData: HistoryVoidData;
  PaylineType: number;

  constructor(paylineType: number, voidData: HistoryVoidData = null) {
    this.PaylineType = paylineType;
    this.HistoryVoidData = voidData;
  }
}

export class Allocation {
  Rate: number;
  Hours: number;
}

// NOTE: these are the values surfaces on tasks and payrolls
export enum AgilePayrollStatuses {
  'Payroll Entry in progress',
  'Approved',
  'Reversed',
  'Payroll Entry On Hold',
  'ReProcessed',
  'Waiting for Reports Processing',
  'Reports Available'
}

export class BankInfo {
  PrefundDays: number;
}

export class BillingCharge {
  BillingType: string;
  Amount: number;

  constructor(type: string, amount: number) {
    this.BillingType = type;
    this.Amount = amount;
  }
}

export class BillingChargeRequest {
  BillingCharge: BillingCharge;
  SuppressNotifications: boolean;
}

export enum BillingTypes {
  BASE = 'Payroll Base',
  ADJUST = 'Adjustment Base',
  NONE = 'No Charge Adjustment'
}

export class CompanyLocationConfigEx extends CompanyLocationConfig {
  NextPayDates: PayDateInfoEx[];
}

export class CreatePayrollArgs {
  ClientCode: string;
  PayDate: Date;
  Calendars: ScheduleRequest[];
  PayType: number;
  LocationSeq: number;
  TimeclockId: number = null;
  ManualCheckIds: number[] = null;
  Label: string = null;
  Config: PayrollConfig;
  PayrollToCorrect: number;
  CorrectionSource: string;

  constructor(
    clientCode: string,
    pdi: PayDateInfo,
    calendars: ScheduleRequest[],
    payType: number,
    config: PayrollConfig,
    timeclockId: number = null,
    manualChecks: ManualCheck[] = null,
    label: string = null,
    correctionSource: string = null
  ) {
    this.ClientCode = clientCode;
    this.Calendars = calendars;
    this.LocationSeq = pdi.LocationId;
    this.PayDate = pdi.TargetPayDate;
    this.PayType = payType;
    this.TimeclockId = timeclockId;
    this.ManualCheckIds = manualChecks?.map(obj => obj.Id);
    this.Label = label;
    this.Config = config;
    this.CorrectionSource = correctionSource;
  }
}

export class Deduction {
  constructor(deduction?: Deduction) {
    this.DeductionCode = deduction ? deduction.DeductionCode : null;
    this.PermanentRate = deduction ? deduction.PermanentRate : null;
    this.PermanentAmount = deduction ? deduction.PermanentAmount : null;
    this.TemporaryRate = deduction ? deduction.TemporaryRate : null;
    this.TemporaryAmount = deduction ? deduction.TemporaryAmount : null;
    this.DeductionPaidBy = deduction ? deduction.DeductionPaidBy : null;
    this.CurrentAmount = deduction ? deduction.CurrentAmount : null;
    this.YTDAmount = deduction ? deduction.YTDAmount : null;
    this.ThirdPartyPayeeId = deduction ? deduction.ThirdPartyPayeeId : null;
    this.HoldThirdPartyDisb = deduction ? deduction.HoldThirdPartyDisb : false;
  }

  DeductionCode: string;
  PermanentRate: number;
  PermanentAmount: number;
  TemporaryRate: number;
  TemporaryAmount: number;
  DeductionPaidBy: string; // either "E" (paid by employee) or "C" (paid by employer/company)
  CurrentAmount: number;
  YTDAmount: number;
  ThirdPartyPayeeId: number;
  IsCheckCombined: boolean; // US 27884
  HoldThirdPartyDisb: boolean; // US 30747/8
}

export class DeliveryOption {
  DeliveryMethodId: number;
  Charge: number;
  DeliveryInstructions: string;
}

export class DirectDeposit {
  DirectDepositId: number;
  TemporaryAmount: number;
  CurrentAmount: number;
}

export class Earning {
  constructor(earning?: Earning) {
    this.EarningId = earning ? earning.EarningId : null;
    this.PermanentRateAmount = earning ? earning.PermanentRateAmount : null;
    this.TemporaryRateAmount = earning ? earning.TemporaryRateAmount : null;
    this.Hours = earning ? earning.Hours : 0;
    this.CurrentAmount = earning ? earning.CurrentAmount : null;
    this.YTDAmount = earning ? earning.YTDAmount : null;
    this.YTDHours ? earning.YTDHours : null;
  }

  EarningId: string;
  PermanentRateAmount: number;
  TemporaryRateAmount: number; // null if user has not made edits
  Hours: number;
  CurrentAmount: number;
  YTDAmount: number;
  YTDHours: number;
}

export class EarningTotals {
  TotalHours: number;
  TotalAmount: number;
}

export class EmployeePay {
  EmployeeNumber: number;
  PersonId: number;
  EmployeeFirstName: string;
  EmployeeMiddleName: string;
  EmployeeLastName: string;
  CheckStubMessage: string;
  PeriodEnd: Date;
  LaborAllocationType: string;
  Paylines: Payline[];
  DivisionId: number;
  DepartmentId: number;
  PayFrequency: number;
}

export class AllocatedEarning {
  PermanentRateAmount: number;
  TemporaryRateAmount: number;
  Hours: number;
  ResultAmount: number;

  constructor(tempRate: number, hours: number) {
    this.TemporaryRateAmount = tempRate;
    this.PermanentRateAmount = tempRate;
    this.Hours = hours;
  }
}

//#region History Void
export class HistoryVoidData {
  PayrollYear: number;
  PayrollSeq: number;
  PaylineId: number;
  CheckNumber: string;

  constructor(year: number, seq: number, paylineId: number, checkNo: string) {
    this.PayrollYear = year;
    this.PayrollSeq = seq;
    this.PaylineId = paylineId;
    this.CheckNumber = checkNo;
  }
}

export class HistoryVoidReportLine {
  EmployeeId: number;
  PayrollSeq: number;
  PayrollYear: number;
  PaylineId: number;
  PayDate: Date;
  EmployeeName: string;
  Amount: number;
  Method: string;
  CheckNo: string;
}

export class ThirdPartyReportLine extends HistoryVoidReportLine {
  Payee: string;
  Deduction: string;
  Combined: string;

  constructor(h: HistoryVoidReportLine) {
    super();
    this.EmployeeId = h.EmployeeId;
    this.PayrollSeq = h.PayrollSeq;
    this.PayrollYear = h.PayrollYear;
    this.PaylineId = h.PaylineId;
    this.PayDate = h.PayDate;
    this.EmployeeName = h.EmployeeName;
    this.Amount = h.Amount;
    this.Method = h.Method;
    this.CheckNo = h.CheckNo;
  }
}

// #endregion
export class LaborAllocation {
  PermanentPercentageAllocations: number;
  TemporaryPercentageAllocations: number;
  TemporaryHourlyAllocations: Record<string, AllocatedEarning>;
  ResultPercentage: number;

  constructor(percent: number = 0) {
    this.PermanentPercentageAllocations = percent;
    this.TemporaryPercentageAllocations = percent;
    this.TemporaryHourlyAllocations = {};
  }
}

export class ManualCheck {
  Id: number;
  CheckNumber: string;
  Name: string;
  Total: number;
  DateCreated: Date;
  PayrollYear: number;
  PayrollSequence: number;
}

export class MasterPayrollObject {
  constructor(
    clientCode: string,
    calendars: Schedule[],
    locationId: number,
    payDate: Date,
    isAutomaticSkipped: boolean,
    isCancelled: boolean,
    status: number,
    createdBy: string,
    createdOn: Date
  ) {
    this.ClientCode = clientCode;
    this.Calendars = calendars;
    this.LocationId = locationId;
    this.PayDate = payDate;
    this.IsAutomaticSkipped = isAutomaticSkipped;
    this.IsCancelled = isCancelled;
    this.Status = status;
    this.CreatedBy = createdBy;
    this.CreatedByUserId = createdBy;
    this.CreatedOn = createdOn;
    this.LastEditBy = createdBy;
    this.LastEditByUserId = createdBy;
    this.LastEditOn = createdOn;
  }

  Id: number;
  // overrides and behavior modifiers for payroll
  Config: PayrollConfig;
  // payline information for each emloyee to pay
  EmployeePays: EmployeePay[];

  // key payroll identifiers
  ClientCode: string;
  Year: number;
  SequenceNumber: number;
  LocationId: number;
  PayDate: Date;
  Contact: string; // client contact that requested the payroll
  Calendars: Schedule[];
  PayType: number;
  IsAutomaticSkipped: boolean;
  IsCancelled: boolean;
  Status: number;
  InputSource: number;
  // NOTE: open question to team if this is needed
  Label: string;

  // blame data
  LastEditBy: string;
  LastEditByUserId: string;
  LastEditOn: Date;
  CreatedBy: string;
  CreatedByUserId: string;
  CreatedOn: Date;

  TimeclockId: number | null;
}

export class LegacyMasterPayrollObject {
  constructor(
    clientCode: string,
    calendars: number[],
    locationId: number,
    periodBegin: Date,
    periodEnd: Date,
    payDate: Date,
    isAutomaticSkipped: boolean,
    isCancelled: boolean,
    status: number,
    createdBy: string,
    createdOn: Date
  ) {
    this.ClientCode = clientCode;
    this.Calendars = calendars;
    this.LocationId = locationId;
    this.PeriodBegin = periodBegin;
    this.PeriodEnd = periodEnd;
    this.PayDate = payDate;
    this.IsAutomaticSkipped = isAutomaticSkipped;
    this.IsCancelled = isCancelled;
    this.Status = status;
    this.CreatedBy = createdBy;
    this.CreatedByUserId = createdBy;
    this.CreatedOn = createdOn;
    this.LastEditBy = createdBy;
    this.LastEditByUserId = createdBy;
    this.LastEditOn = createdOn;
  }

  Id: number;
  // overrides and behavior modifiers for payroll
  Config: PayrollConfig;
  // payline information for each emloyee to pay
  EmployeePays: EmployeePay[];

  // key payroll identifiers
  ClientCode: string;
  Year: number;
  SequenceNumber: number;
  LocationId: number;
  PeriodBegin: Date;
  PeriodEnd: Date;
  PayDate: Date;
  Contact: string; // client contact that requested the payroll
  Calendars: number[];
  PayType: number;
  IsAutomaticSkipped: boolean;
  IsCancelled: boolean;
  Status: number;
  InputSource: number;
  // NOTE: open question to team if this is needed
  Label: string;

  // blame data
  LastEditBy: string;
  LastEditByUserId: string;
  LastEditOn: Date;
  CreatedBy: string;
  CreatedByUserId: string;
  CreatedOn: Date;
  TimeclockId: number | null;
}

export class PayDateInfo {
  AssignedSpecialist: string;
  CalendarName: string;
  CalendarId: number;
  ClientId: string;
  ClientName: string;
  Frequency: PayrollFrequency;
  PayrollInputMethod: string;
  LocationId: number;
  PeriodEnd: Date; // date when the pay period ends
  ProcessDueDate: Date; // send payrolls to agile API on this date
  TargetPayDate: Date; // day when checks actually go out
  PeriodBegin: Date;
}

export class PayDateInfoEx extends PayDateInfo {
  // when the prefund window for this calendar ACTUALLY begins, given the location's banking
  // prefundDays setting
  ActualAchPrefundWindowStart: Date;

  // when the pay date would need to be to conform with the location's banking prefund days setting
  PotentiallyValidAchPrefundPayDate: Date;

  // if client is tax filing, how many days until pay date
  ActualTaxFilingPrefundDays: number;

  RequiredAchPrefundDays: number;

  // how many days are actually required for tax filing prefunding (usually 2)
  RequiredTaxFilingPrefundDays: number;

  ShowAchPrefundWarnings: boolean;

  ShowTaxFilingPrefundWarnings: boolean;
}

export class PayGridSessionData {
  ClientId: string;
  PayrollYear: number;
  PayrollSequenceNumber: number;
  // keyed on personId, paylineId
  ChangedColumns: Record<number, Record<number, string[]>>;
  // unique rowIds. `person-${pay.PersonId}-payline-${line.PaylineId}`,
  DetailedRowIds: string[] = [];
  // task 14671
  // rows that have held earnings/deductions/etc have their temp values
  // cleared on first paygrid calculation. track those paylines here so this opertaion
  // only happens on first calculation.
  // keyed on employeeNumber, paylineId
  CalculatedPaylines: Record<number, number[]>;
  PayrollModified: boolean;

  constructor(payroll: MasterPayrollObject) {
    this.ClientId = payroll.ClientCode;
    this.PayrollYear = payroll.Year;
    this.PayrollSequenceNumber = payroll.SequenceNumber;
    this.ChangedColumns = {};
    this.DetailedRowIds = [];
    this.CalculatedPaylines = {};
    this.PayrollModified = false;
  }
}

export class PayGridSetupError {
  PayGridId: number;
  // apollo has a paygrid record, but payroll was cancelled in the base
  MissingPayrollRecord: boolean;
}

export class Payline {
  PaylineId: number;
  OkToPay: boolean;
  Earnings: Earning[];
  Taxes: Tax[];
  Deductions: Deduction[];
  DirectDeposits: DirectDeposit[];
  PayFrequency: PayrollFrequency; // Task 7532
  QTDEarningsGross: number; // null on initial pay grid load
  NetAmountPaid: number;
  NetCheckAmountPaid: number;
  NetAmountDeposited: number;
  EarningsTotalCurrentHours: number;
  EarningsTotalYTDHours: number;
  EarningsTotalCurrentAmount: number;
  EarningsTotalYTDAmount: number;
  TaxesTotalCurrentAmount: number;
  DeductionsTotalAmount: number;
  Errors: ErrorDetail[];
  // used on the front end only, populated by PayGridSessionData
  ChangedColumns: string[]; // list of string column ids that have changed
  HoldTimeOffAccruals: boolean = false;
  PeriodEndOverride: Date;
  PayFrequencyTaxOverride: number; // refer to PayrollFrequency enum for acceptable values
  PaylineType: PaylineType;
  HistoryVoidData: HistoryVoidData;
  Allocations: Record<string, LaborAllocation>;
  CanEdit: boolean;
  OverrideCheckNumber: string;
  HasBeenPrinted: boolean;
  PaylineSeq: number; // US 26864. seq that paylines get calculated in APS
  //Used to trigger immediate calculation after stitching in Time Off
  TriggerImmediateCalculation: boolean;

  // correction payroll fields
  IsVoided?: string;
  VoidedPayId?: number;
  VoidedSeqNum?: number;

  UserConfirmedNonRegularPayline?: boolean;
}

export enum PaylineType {
  Normal = 0,
  NoCheck = 1,
  Void = 2,
  Manual = 3,
  ThirdPartySick = 4
}

export class PayrollApprovalTotals {
  PayrollDebit: number;
  TaxFilingDebit: number;
  ThirdPartyDisbursementAch: number;
  BillingDebit: number;
  TotalUncollectedTax: number;
  NetChecks: number;
  ThirdPartyDisbursementChecks: number;
}

export class PayrollConfig {
  constructor() {
    this.HoldDeductions = [];
    this.HoldEarnings = [];
    this.HoldTaxes = [];
    this.HoldDirectDeposit = false;
    this.PayrollFrequencyOverride = null;
    this.HoldTimeOff = [];
  }

  HoldDeductions: string[];
  HoldEarnings: string[];
  HoldTaxes: TaxHold[];
  HoldDirectDeposit: boolean;
  PayrollFrequencyOverride: number;
  HoldTimeOff: number[];

  public hasOverrides(): boolean {
    return (
      this.HoldEarnings?.length > 0 ||
      this.HoldDeductions?.length > 0 ||
      this.HoldTaxes?.filter(t => t.Id != null).length > 0 ||
      this.HoldDirectDeposit ||
      this.PayrollFrequencyOverride != null ||
      this.HoldTimeOff?.length > 0
    );
  }
}

export class PayrollConfigSetupData {
  Earnings: CompanyEarningConfig[];
  Deductions: CompanyDeductionConfig[];
  PtoAccruals: IdNamePair[];
  Reports: IdNamePair[];
  Disbursements: IdNamePair[];
  DeliveryOptions: DeliveryOption[];
  // NOTE: this is currently stitched in on the front end only
  // on the payroll start init logic
  WithholdingTaxes: CompanyTaxCodeConfig[];
  NextAvailableCheckNumber: number;
  NextAvailableVoucherNumber: number;
  SortFields: number[];
  HasDivisions: boolean;
  HasDepartments: boolean;
}

export enum PayrollInputSources {
  Workstation = 0, // base agile app
  Internet = 1, // legacy agile web app
  Automatic = 2,
  MyPayrollDepartment = 3,
  API = 4 // apollo
}

export class PayrollStartConfig {
  Errors: string[];
  IsLocked: boolean;
  Warnings: string[];
  Unsupported: string[];

  constructor() {
    this.Errors = [];
    this.Warnings = [];
    this.IsLocked = false;
    this.Unsupported = [];
  }
}

export interface PrefundInfo {
  IsInPrefund: boolean;
  PrefundStartDate: Date;
  CheckTime: Date;
}

export class PayrollStartData {
  IsEmployeeAchAllowed: boolean;
  ActivePayroll: MasterPayrollObject;
  Locations: CompanyLocationConfigEx[];
  QuarterStatus: QuarterStatuses;
  OpenOrHeldQuarterPayrolls: UsedPayroll[];
  HasPayrollLastEditorSessionExpired: boolean;
}

export enum PayrollWorksheetStatuses {
  Cancelled = 0, // worksheet has been "skipped"
  Ready = 1, // no worksheet generated
  InProgress = 2 // worksheet has been started
}

export enum PayTypes {
  Regular = 0,
  Adjustment = 1,
  Bonus = 2,
  OffCycle = 3,
  Reversed = 4,
  Manual = 5,
  ReProcessed = 6
}

// these values are reflected to what Agile uses
// values 11 up to 89 are report generating failure and are not passed
export enum PayrollStatuses {
  Started = 1, // NOTE: this is equivalent to "In Process"
  Approved = 2,
  Saved = 3,
  Reversed = 4,
  Hold = 5, // NOTE: this is equivalent to "On Hold"
  ReProcessed = 6,
  Complete = 89,
  Check = 11
}

export class ScheduleRequest {
  CalendarId: number;
  SeqNo: number;
  PeriodEnd: Date;

  constructor(calendarId: number, seqNo: number, periodEnd: Date) {
    this.CalendarId = calendarId;
    this.SeqNo = seqNo;
    this.PeriodEnd = periodEnd;
  }
}

export class Schedule extends ScheduleRequest {
  Frequency: number;
  ScheduleName: string;
  PeriodBegin: Date;
  ScheduledPayDate: Date;
  ScheduledPeriodEnd: Date;

  constructor(
    calendarId: number,
    seqNo: number,
    periodEnd: Date,
    frequency: number,
    name: string,
    begin: Date,
    scheduledEnd: Date,
    payDate: Date
  ) {
    super(calendarId, seqNo, periodEnd);
    this.Frequency = frequency;
    this.ScheduleName = name;
    this.PeriodBegin = begin;
    this.ScheduledPeriodEnd = scheduledEnd;
    this.ScheduledPayDate = payDate;
  }
}

export class Tax {
  TaxCode: string;
  CalculationType: string;
  FixedAdditional: number;
  Amount: number;
  TemporaryCalculationType: string;
  TemporaryAmount: number;
  TaxPaidBy: string; // either "E" (paid by employee) or "C" (paid by employer/company)
  FilingStatus: string;
  Allowances: number; // NULL for taxes that do not have allowances
  CurrentAmount: number;
  YTDAmount: number;
  AllowOverrideSecondPays: boolean; // task 10088
}

export class TaxHierarchy {
  TaxType: string;
  TypeName: string;
  Hierarchy: number;
}

export class TaxHold {
  constructor(id?: string) {
    this.Id = id;
    this.OverridePercentage = 0;
  }

  Id: string;
  OverridePercentage: number;
}

export class ThirdPartyPayee {
  Id: number;
  Description: string;
  Name: string;
  PaymentMethod: number; // 1 = check, 2 = ACH
}

export class ThirdPartyPayeeReport {
  Description: string;
  Payee: string;
  PaidBy: string;
  Method: string;
  Amount: number;
  AppearanceSequence: number;
}

export class TimeClockSelection {
  TimeClockFile?: TimeclockModel;
}
export interface TimeclockFile {
  Id: number;
  ClientCode: string;
  PeriodEnd: Date;
  PayrollFrequency?: number;
  PayrollYear?: number;
  FileName: string;
  PersistedToAgile: boolean;
  Details: TimeclockDetail[];
}

export interface TimeclockDetail {
  DetailId?: number;
  EmployeeId?: number;
  PersonId?: number;
  EmployeeName: string;
  EarningCode: string;
  Rate?: number;
  Hours?: number;
  Amount?: number;
  DivisionNumber?: number;
  DepartmentNumber?: number;
  JobCode: string;
  LocationId?: number;
}

export interface TimeclockFileUploadResult {
  Header: TimeclockModel;
  Validation: TimeclockValidation;
  Id: number;
}

export class TimeclockModel {
  Id: number;
  PeriodEndDate: Date;
  FileName: string;
  IsValid: boolean;
  Messages: ErrorDetail[];
  PersistedToAgile: boolean;

  constructor(id: number, pe: Date, valid: boolean, messages: ErrorDetail[]) {
    this.Id = id;
    this.PeriodEndDate = pe;
    this.IsValid = valid;
    this.Messages = messages;
  }
}
export interface TimeclockValidation {
  Record: TimeclockDetail;
  ValidationResult: TimeclockValidationResult;
}
export interface TimeclockValidationResult {
  FirstLastNameMatch: boolean;
  DivisionRequired: boolean;
  DivisionNotFound: boolean;
  JobCodeMissing: boolean;
  NoDivisionMatch: boolean;
  NoDepartmentMatch: boolean;
  NoSSNMatch: boolean;
  EmployeeNumberMissing: boolean;
  EmployeeIdMissing: boolean;
  EarningNotActive: boolean;
  EarningNotOnClient: boolean;
  EarningNameMissing: boolean;
  EarningNotOnEmployee: boolean;
  DepartmentRequired: boolean;
  DepartmentNotOnClient: boolean;
  JobCodeNotOnClient: boolean;
  IsValid: boolean;
}

// #region shared service event args
export class SelectedPaylineArgs {
  RowId: string;
  PaylineId: number;
  PersonId: number;
  RowIndex: number;
  Payline: Payline;

  constructor(
    rowId: string,
    paylineId: number,
    personId: number,
    rowIndex: number,
    payline: Payline = null
  ) {
    this.RowId = rowId;
    this.PaylineId = paylineId;
    this.PersonId = personId;
    this.RowIndex = rowIndex;
    this.Payline = payline;
  }
}

export class OpenPaylineDetailsArgs extends SelectedPaylineArgs {
  NeedsCalculation: boolean;
  IsDirty: boolean;
  IsReadOnly: boolean;
  OpenInModal: boolean = true;

  constructor(
    rowId: string,
    paylineId: number,
    personId: number,
    rowIndex: number,
    needsCalc: boolean,
    dirty: boolean,
    isReadOnly: boolean,
    openInModal: boolean = true,
    payline: Payline = null
  ) {
    super(rowId, paylineId, personId, rowIndex);
    this.NeedsCalculation = needsCalc;
    this.IsDirty = dirty;
    this.IsReadOnly = isReadOnly;
    this.OpenInModal = openInModal;
    this.Payline = payline;
  }
}

export class SharedPayrollInfo {
  ClientCode: string;
  Year: number;
  SequenceNumber: number;
  Status: number;
  LocationId: number;
  CalendarId: number;
  PayDate: Date;

  constructor(
    clientCode: string,
    year: number,
    seq: number,
    status: number,
    locationId: number,
    calendarId: number,
    payDate: Date
  ) {
    this.ClientCode = clientCode;
    this.Year = year;
    this.SequenceNumber = seq;
    this.Status = status;
    this.LocationId = locationId;
    this.CalendarId = calendarId;
    this.PayDate = payDate;
  }
}

export class SubmitApprovalRequestBody {
  SuppressNotifications?: boolean;

  constructor(suppressNotifications?: boolean) {
    this.SuppressNotifications = suppressNotifications;
  }
}

export class UsedPayroll {
  PayrollSeq: number;
  Year: string;
  LocationId: number;
  Status: number;
  Type: number;
  EnteredDate: Date;
  PayDate: Date;
  Label: string;
  UserID: string;
  Calendars: Schedule[];

  constructor(seq: number, year: string, payDate: Date) {
    this.PayrollSeq = seq;
    this.Year = year;
    this.PayDate = payDate;
  }
}

// #endregion
